"use client";

import { useRef } from "react";

import { Button, Container, Group, Title } from "@mantine/core";
import { sendGTMEvent } from "@next/third-parties/google";
import Link from "next/link";

import { SIGNUP_CTA, TALK_TO_HUMAN_CTA } from "@/app/(marketing)/constants";

import DarkBand from "../DarkBand";
import { useVisibleDelay } from "../motion/useVisibleDelay";
import classes from "./Hero.module.css";
import OperatingModelTerm from "./OperatingModelTerm";
import { usePenPlacement } from "./usePenPlacement";
import { ProtectedEmailButton } from "@/app/lib/components/ProtectedEmail";

/** The hero shows clean first; the pen note writes itself in this long after
    the page is in front of the reader. */
const PEN_WRITE_DELAY_MS = 3000;

/**
 * The homepage hero: the headline and the line under it, a founder's pen note
 * aside, and the two ways in. Type only, centered on the thread the page runs
 * down its middle, the way the problem section's headline sits under it.
 */
export default function Hero() {
  const wrapRef = useRef<HTMLDivElement>(null);
  const targetRef = useRef<HTMLSpanElement>(null);
  const penRef = useRef<HTMLDivElement>(null);
  const arrowRef = useRef<SVGSVGElement>(null);
  const isPenWritten = useVisibleDelay(PEN_WRITE_DELAY_MS);

  usePenPlacement(wrapRef, targetRef, penRef, arrowRef);

  return (
    <DarkBand className={classes.hero}>
      <Container size="lg" className={classes.container}>
        <div className={classes.copy}>
          <div className={classes.headlineWrap} ref={wrapRef}>
            <Title order={1} className={classes.headline}>
              Keep your judgment <span className={classes.noWrap}>in the loop.</span>
              <br />
              <span className={classes.line}>
                A <span className={classes.noWrap}>founder-mode</span> <OperatingModelTerm />
                <br />{" "}
                <span className={classes.noWrap}>
                  for your{" "}
                  <span className={classes.noWrap} ref={targetRef}>
                    people and agents.
                  </span>
                </span>
              </span>
            </Title>

            {/* Hidden before its write-in only under [data-motion], so a
                reader without the script, or with reduced motion, sees it. */}
            <div className={classes.pen} ref={penRef} data-written={isPenWritten || undefined}>
              <svg className={classes.penArrow} ref={arrowRef} viewBox="0 0 66 50" aria-hidden>
                <path pathLength={1} d="M62 44 C 46 42, 24 34, 13 9" />
                <path pathLength={1} d="M6 17 L13 7 L23 13" />
              </svg>
              <span className={classes.penText}>
                especially your slop&nbsp;cannons and vibe&nbsp;coders
              </span>
            </div>
          </div>

          {/* Mantine's own buttons at md, primary first as in the close, centered
              under the type. Talk to a human is subtle in the fixed shade
              gray.3, so its hover reads on the dark band. */}
          <Group gap="sm" justify="center" className={classes.actions}>
            <Button
              component={Link}
              href={SIGNUP_CTA.href}
              variant="gradient"
              gradient={{ from: "blue", to: "cyan", deg: 90 }}
              size="md"
              onClick={() =>
                sendGTMEvent({
                  ...SIGNUP_CTA.gtm,
                  page: "splash",
                  location: "hero",
                  button_style: "gradient",
                })
              }
            >
              {SIGNUP_CTA.label}
            </Button>
            <ProtectedEmailButton
              address={TALK_TO_HUMAN_CTA.email}
              variant="subtle"
              color="gray.3"
              size="md"
            >
              {TALK_TO_HUMAN_CTA.label}
            </ProtectedEmailButton>
          </Group>
        </div>
      </Container>
    </DarkBand>
  );
}
